import { useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
// arch-ignore mui-pole -- renderInput Autocomplete wymaga TextField; wspólny AutoComplete nie ma wartości kontrolowanej ani czyszczenia (wzorzec JrwaSignaturePicker)
import { Autocomplete, TextField } from "@mui/material";

import ebiOfficeService from "api/ebiOfficeService";
import jrwaService, { IJrwaClassification, jrwaLabel } from "api/jrwaService";
import workspaceService from "api/workspaceService";
import Accordion from "components/Accordion";
import Button from "components/Button";
import { IDropdownObject } from "components/Dropdown/typings";
import DropdownMui from "components/DropdownMui";
import HollowButton from "components/HollowButton";
import Loading from "components/Loading";
import SecondCheckbox from "components/SecondCheckbox";
import { IEbiSubmissionFormHandling } from "dto/Ebi/IEbiOffice";
import { IError } from "dto/IError";
import { getCompanyId } from "utils/getCompanyId";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";
import { EBI_DEFAULT_OFFICES, EBI_FORM_OFFICES, EBI_FORMS } from "modules/ebiForms/catalog";

import styles from "./styles.module.scss";
import { FC } from "./typings";

// Podzakładka „Wnioski”: które pozycje katalogu (79 postępowań gminy, jej jednostek, starostwa i jednostek powiatu)
// petenci mogą składać w portalu tego urzędu. Pozycje pogrupowane według części katalogu; wybór domyślny (nic nie
// zapisano) = wnioski gminne — „Przywróć domyślne” zapisuje go jako domyślny, żeby nowe wnioski gminne dochodziły same.
// Przy włączonym wniosku urząd wskazuje pracownika, który go obsługuje (złożony wniosek omija wtedy biuro podawcze i po
// rejestracji jest od razu dekretowany na tę osobę), oraz kod JRWA, którego wniosek dotyczy (trafia do metryki przesyłki).

type Handling = Record<string, IEbiSubmissionFormHandling>;

const sameCodes = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((code) => b.has(code));

const DEFAULT_CODES = new Set(EBI_FORMS.filter((form) => EBI_DEFAULT_OFFICES.includes(form.office)).map((form) => form.code));

const GROUPS = EBI_FORM_OFFICES.map((office) => ({ office, forms: EBI_FORMS.filter((form) => form.office === office) }));

// aktywny członek firmy (BE WorkspaceMemberStatus.Active)
const ACTIVE_MEMBER = 4;

// pozycja „Biuro podawcze” (bez pracownika): wartość niepusta — przy pustej MUI nie podnosi etykiety pola nad tekst
const MAILROOM = "mailroom";

const toHandling = (list: IEbiSubmissionFormHandling[] | undefined): Handling =>
  Object.fromEntries((list ?? []).map((item) => [item.Code, item]));

// pozycje z pracownikiem albo kodem, w kolejności katalogu — w tej postaci zapisuje je BE
const handlingList = (handling: Handling): IEbiSubmissionFormHandling[] =>
  EBI_FORMS.map((form) => handling[form.code])
    .filter((item) => !!item && (!!item.HandlerUserId || !!item.ClassificationId))
    .map((item) => ({ Code: item.Code, HandlerUserId: item.HandlerUserId ?? null, ClassificationId: item.ClassificationId ?? null }));

const sameHandling = (a: Handling, b: Handling) => JSON.stringify(handlingList(a)) === JSON.stringify(handlingList(b));

// jak przy zakładaniu teczki: symbol od początku albo fragment hasła, najwyżej 200 podpowiedzi
const filterJrwa = (options: IJrwaClassification[], { inputValue }: { inputValue: string }) => {
  const query = inputValue.trim().toLowerCase();
  const found = query
    ? options.filter((o) => o.Code.toLowerCase().startsWith(query) || o.Description.toLowerCase().includes(query))
    : options;

  return found.slice(0, 200);
};

const EbiFormsTab: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, error, isLoading } = ebiOfficeService.useGetSubmissionForms();
  const { mutate: save, isLoading: isSaving } = ebiOfficeService.useSaveSubmissionForms();
  const { data: workspaceData, isLoading: isLoadingMembers } = workspaceService.useGetWorkspace(getCompanyId() ?? "");
  const { data: jrwaData, isLoading: isLoadingJrwa } = jrwaService.useGetList();

  const settings = data?.data;
  const saved = useMemo(() => new Set(settings?.Codes ?? []), [settings]);
  const savedHandling = useMemo(() => toHandling(settings?.Handling), [settings]);

  // wybór z formularza; null = bez zmian (lista pokazuje zapisany)
  const [draft, setDraft] = useState<Set<string> | null>(null);
  // „Przywróć domyślne” — zapis bez listy kodów (BE: wybór domyślny)
  const [restoreDefault, setRestoreDefault] = useState(false);
  // obsługa wniosków z formularza; null = bez zmian
  const [handlingDraft, setHandlingDraft] = useState<Handling | null>(null);

  const value = draft ?? saved;
  const handling = handlingDraft ?? savedHandling;
  const handlingDirty = !!handlingDraft && !sameHandling(handlingDraft, savedHandling);
  const dirty = (!!draft && !sameCodes(draft, saved)) || (restoreDefault && !settings?.IsDefault) || handlingDirty;
  const canEdit = !!settings?.CanEdit;
  const canSave = canEdit && !!settings?.HasSettings;
  const count = EBI_FORMS.filter((form) => value.has(form.code)).length;

  const members = useMemo(
    () =>
      (workspaceData?.data?.Members ?? [])
        .filter((m) => !!m.UserId && m.Status === ACTIVE_MEMBER)
        .sort((a, b) => a.Label.localeCompare(b.Label, "pl")),
    [workspaceData],
  );

  const handlerOptions = useMemo<IDropdownObject[]>(
    () => [{ value: MAILROOM, name: t("ebiOffice.forms.mailroom") }, ...members.map((m) => ({ value: m.UserId, name: m.Label }))],
    [members, t],
  );

  // pracownik zapisany wcześniej, który odszedł z urzędu — zostaje widoczny, żeby urząd go zmienił (BE kieruje taki wniosek do BP)
  const handlerList = (handlerId: string | null) =>
    !handlerId || members.some((m) => m.UserId === handlerId)
      ? handlerOptions
      : [...handlerOptions, { value: handlerId, name: t("ebiOffice.forms.handlerInactive") }];

  const jrwaItems = useMemo(() => jrwaData?.data?.Items ?? [], [jrwaData]);
  const jrwaById = useMemo(() => new Map(jrwaItems.map((c) => [c.Id, c])), [jrwaItems]);
  const jrwaActive = useMemo(() => jrwaItems.filter((c) => !c.IsArchived), [jrwaItems]);

  // kod zarchiwizowany po przypisaniu zostaje na liście, żeby pole pokazało zapisaną wartość
  const jrwaOptions = (classification: IJrwaClassification | null) =>
    classification?.IsArchived ? [classification, ...jrwaActive] : jrwaActive;

  // jak w zakładce Wygląd: odpowiedź bez komunikatu = serwer bez tej trasy
  const errorText = error ? getErrorMessage(error as IError) || t("ebiOffice.forms.unsupported") : null;

  const update = (codes: Set<string>) => {
    setDraft(codes);
    setRestoreDefault(false);
  };

  const toggle = (code: string, on: boolean) => {
    if (!canEdit) return;

    const next = new Set(value);
    if (on) next.add(code);
    else next.delete(code);
    update(next);
  };

  const toggleGroup = (codes: string[], on: boolean) => {
    const next = new Set(value);
    codes.forEach((code) => (on ? next.add(code) : next.delete(code)));
    update(next);
  };

  const updateHandling = (code: string, change: Partial<IEbiSubmissionFormHandling>) => {
    if (!canEdit) return;

    const current = handling[code] ?? { Code: code, HandlerUserId: null, ClassificationId: null };
    setHandlingDraft({ ...handling, [code]: { ...current, ...change } });
  };

  const handleRestoreDefault = () => {
    setDraft(new Set(DEFAULT_CODES));
    setRestoreDefault(true);
  };

  const handleCancel = () => {
    setDraft(null);
    setRestoreDefault(false);
    setHandlingDraft(null);
  };

  const notify = (type: ENotificationType, text: string) => notification({ type, title: t("ebiOffice.forms.title"), text });

  const handleSave = () => {
    const codes = restoreDefault ? null : EBI_FORMS.filter((form) => value.has(form.code)).map((form) => form.code);

    save(
      { Codes: codes, Handling: handlingDirty ? handlingList(handling) : null },
      {
        onSuccess: ({ data: savedSettings }) => {
          queryClient.setQueryData(ebiOfficeService.queryKeys.getSubmissionForms, { data: savedSettings });
          handleCancel();
          notify(ENotificationType.SUCCESS, t("ebiOffice.forms.saved"));
        },
        onError: (e) =>
          notify(
            ENotificationType.ERROR,
            getErrorMessage(e as IError) || (e instanceof Error ? e.message : "") || t("errors.somethingWentWrongShort"),
          ),
      },
    );
  };

  const ready = !!settings && !isLoadingMembers && !isLoadingJrwa;

  return (
    <div className={styles.wide}>
      <div className={styles.section}>
        <div className={styles.sectionTitle}>{t("ebiOffice.forms.title")}</div>
        <div className={styles.hint}>{t("ebiOffice.forms.description")}</div>
        <div className={styles.hint}>{t("ebiOffice.forms.handlingHint")}</div>
        {errorText && <div className={styles.error}>{errorText}</div>}
        {settings && !canEdit && <div className={styles.hint}>{t("ebiOffice.forms.readOnly")}</div>}
        {settings && canEdit && !settings.HasSettings && <div className={styles.hint}>{t("ebiOffice.forms.needsSettings")}</div>}
        {settings && (
          <>
            {settings.IsDefault && !draft && <div className={styles.text}>{t("ebiOffice.forms.default")}</div>}
            <div className={styles.text}>{t("ebiOffice.forms.count", { count, total: EBI_FORMS.length })}</div>
            {count === 0 && <div className={styles.error}>{t("ebiOffice.forms.none")}</div>}
          </>
        )}

        {settings && canEdit && (
          <div className={styles.actions}>
            <HollowButton
              text={t("ebiOffice.forms.restoreDefault")}
              onClick={handleRestoreDefault}
              disabled={isSaving || (settings.IsDefault && !draft)}
            />
            {dirty && <HollowButton text={t("ebiOffice.forms.cancel")} onClick={handleCancel} disabled={isSaving} />}
            <Button text={t("ebiOffice.forms.save")} onClick={handleSave} disabled={!dirty || !canSave || isSaving} />
          </div>
        )}
      </div>

      {(isLoading || (settings && !ready)) && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}

      {ready && (
        <div className={styles.formGroups}>
          {GROUPS.map(({ office, forms }, index) => {
            const codes = forms.map((form) => form.code);
            const selected = codes.filter((code) => value.has(code)).length;
            const allSelected = selected === codes.length;

            return (
              <Accordion
                key={office}
                // pierwsza część katalogu (urząd gminy) rozwinięta na start
                defaultOpen={index === 0}
                label={
                  <div className={styles.formGroupHeader}>
                    <div className={styles.sectionTitle}>
                      {t(`ebiOffice.forms.office.${office}`)} ({selected}/{codes.length})
                    </div>
                    {canEdit && (
                      <HollowButton
                        text={allSelected ? t("ebiOffice.forms.selectNone") : t("ebiOffice.forms.selectAll")}
                        onClick={() => toggleGroup(codes, !allSelected)}
                        disabled={isSaving}
                        // przycisk w belce sekcji — bez zwijania/rozwijania grupy
                        preventDefault
                      />
                    )}
                  </div>
                }
              >
                <div className={styles.formList}>
                  {forms.map((form) => {
                    const enabled = value.has(form.code);
                    const handlerId = handling[form.code]?.HandlerUserId ?? null;
                    const classificationId = handling[form.code]?.ClassificationId ?? null;
                    const classification = (classificationId && jrwaById.get(classificationId)) || null;

                    return (
                      <div key={form.code} className={styles.formRow}>
                        <div className={styles.formItem}>
                          <SecondCheckbox
                            value={enabled}
                            onChange={(on) => toggle(form.code, on)}
                            text={`${form.code} · ${form.title}`}
                          />
                          {form.external && (
                            <div className={styles.formItemHint}>
                              {t("ebiOffice.forms.external", { service: form.external.service })}
                            </div>
                          )}
                        </div>

                        {enabled && !form.external && (
                          <div className={styles.formHandling}>
                            <DropdownMui
                              className={handlerId ? "" : styles.handlerDefault}
                              label={t("ebiOffice.forms.handler")}
                              list={handlerList(handlerId)}
                              defaultValue={handlerId ?? MAILROOM}
                              onChange={(item?: IDropdownObject) =>
                                updateHandling(form.code, {
                                  HandlerUserId: item?.value && item.value !== MAILROOM ? String(item.value) : null,
                                })
                              }
                              disabled={!canEdit || isSaving}
                            />
                            <Autocomplete<IJrwaClassification>
                              options={jrwaOptions(classification)}
                              value={classification}
                              disabled={!canEdit || isSaving}
                              getOptionLabel={jrwaLabel}
                              isOptionEqualToValue={(o, v) => o.Id === v.Id}
                              filterOptions={filterJrwa}
                              onChange={(_, item) => updateHandling(form.code, { ClassificationId: item?.Id ?? null })}
                              renderOption={({ key, ...props }, o) => (
                                <li key={key} {...props}>
                                  <span className={styles.jrwaCode}>{o.Code}</span>
                                  <span>
                                    {o.Description}
                                    {o.IsArchived && ` (${t("ebiOffice.forms.jrwaArchived")})`}
                                  </span>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t("ebiOffice.forms.jrwa")}
                                  placeholder={t("ebiOffice.forms.jrwaPlaceholder")}
                                  variant="filled"
                                  size="small"
                                />
                              )}
                              noOptionsText={t("ebiOffice.forms.jrwaNone")}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Accordion>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EbiFormsTab;
